<script lang="ts">
  /**
   * SetDetailSheet — a full-screen overlay for one set. Opens when the ribbon
   * fires { kind: "set-detail", setIndex }. Follows the RingConfigSheet overlay
   * pattern (scrim + sheet, lock/unlock body scroll, Escape-to-close), but
   * full-screen rather than a bottom sheet.
   *
   * Layout (set-view redesign v2, 2026-06-17), top to bottom:
   *   1. Back pill "‹ My swim" (wet-hands target)
   *   2. Title + meta ("475m · 19 lengths · 8:51 of swimming") — numbers live here
   *   3. Palindrome callout pill (a real sentence, only when the set is the Rockalita)
   *   4. Story card: story sentence + effort badge (suppressed on warmup/cooldown) +
   *      Fastest/Slowest length chips + the per-set ECharts red-line HR chart
   *      (SetHrLineChart) + "{avg} avg · {peak} peak BPM".
   *   5. "Lengths" header on the left + "Fix a stroke ›" action on the right.
   *   6. Length cards: stroke chip · "{n} · stroke name" (number muted, name bold) ·
   *      time, with an always-visible "{N} strokes · {N}s" detail line (the two raw
   *      facts, elevated above text-muted, no fused composite — G9). Tap opens the
   *      inline stroke picker (no swipe). A length the owner corrected also wears a
   *      "✓ Fixed" mark, and its row's aria-label says what the watch had recorded.
   *   7. "Your watch picked these strokes, except the ones you fixed." footnote
   *      (it used to claim ALL labels were the watch's — false the moment corrections
   *      shipped), then a big Done button.
   *
   * Copy rules enforced: no fused "SWOLF"/"Glide" score anywhere, only the raw
   * strokes-per-length and seconds-per-length facts (G9); set-level HR shows as
   * avg/peak BPM (owner-approved 2026-06-17); stroke names always spelled out;
   * high-contrast, low-vision sizing on every load-bearing string.
   */
  import { tick } from "svelte";
  import type { DerivedWorkout } from "../lib/lapAnalysis";
  import type { Lap } from "../lib/laps";
  import { strokeColor, contrastTextOn } from "../lib/designTokens";
  import { storyFor } from "../lib/setStory";
  import { setEffortBadge, effortColor } from "../lib/effortWords";
  import {
    priorLabelFor,
    upsertLabel,
    buildStrokeCorrection,
    buildWatchConfirmation,
  } from "../lib/swipeDeckLogic";
  import { tapGuard } from "../lib/actions/tapGuard";
  import { closeOverlay } from "../lib/overlay";
  import { get } from "svelte/store";
  import { currentLabels, saveStatus } from "../lib/state";
  import { commitLabelChange } from "../lib/liveCorrection";
  import { lockBodyScroll, unlockBodyScroll } from "../lib/native/bodyScrollLock";
  import { sheetDrag } from "../lib/sheetDrag";
  import { haptic } from "../lib/haptics";
  import { fmtMSS, fmtRest, fmtSeconds1, fmtTimeOfDay } from "../lib/format";
  import { setHrStats } from "../lib/setHrWindow";
  import { buildSetHrLineChart } from "../lib/setHrLineChart";
  import SetHrLineChart from "../components/SetHrLineChart.svelte";
  import {
    setBestWorst,
    setAvgHrIncluded,
    allLapsExcluded,
    looksOffLine,
    swimLacksHeartRate,
    setTimeRange,
  } from "../lib/setMath";
  import { showcase } from "../lib/nav";

  interface Props {
    open?: boolean;
    dw: DerivedWorkout;
    setIndex: number;
    /** When true, jump straight to the first suspect's picker on open (the
     *  Swim-tab suspects card deep-link). One-shot per open. */
    fixFirst?: boolean;
    /** deck tap-out: open THIS length's picker. Wins over fixFirst when both
     *  are present — the suspect deck's "Something else…" already knows
     *  exactly which length it wants, so falling back to "first suspect"
     *  would deep-link to the wrong length. One-shot per open. */
    fixLapIndex?: number;
  }
  let { open = false, dw, setIndex, fixFirst = false, fixLapIndex = undefined }: Props = $props();

  let set = $derived(dw.setGroups[setIndex]);

  // ----- mislabel suspects scoped to this set -----
  // Suspects scoped to the lengths shown in THIS set (baseline stays swim-wide).
  let setSuspects = $derived(
    dw.suspects.filter((s) => set.laps.some((l) => l.index === s.lapIndex)),
  );
  let suspectByIndex = $derived(new Map(setSuspects.map((s) => [s.lapIndex, s])));

  // ----- owner corrections ("mark everywhere" override, review 2026-07-12) -----
  // Swim-wide index, same key space as dw.suspects — matches the ribbon's mark.
  let correctedLapIndices = $derived(dw.corrections.changedLapIndices);

  // The "Fix a stroke ›" header button (and formerly the nudge banner) takes
  // the owner to the first suspect's picker. Falls back to the first lap when
  // there are no suspects so the button is always functional.
  async function reviewFirstSuspect() {
    const target = setSuspects[0] ?? set?.laps[0];
    if (!target) return;
    const lapIndex = "lapIndex" in target ? target.lapIndex : (target as Lap).index;
    pickerIndex = lapIndex;
    await tick();
    document
      .querySelector(`[data-lap-index="${lapIndex}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  // ----- derived facts (numbers only here, never in the story sentence) -----

  /** Set-average HR across INCLUDED laps (setMath), skipping nulls; null when
   *  no HR. A phantom/excluded lap's HR must not color the effort badge. */
  let setAvgHr = $derived(set ? setAvgHrIncluded(set) : null);

  let badge = $derived(setEffortBadge(setAvgHr, dw.maxHr));

  /** True when the set is a warmup or cooldown. Effort badge is suppressed on
   *  these sets regardless of HR — the effort label contradicts the warm-up
   *  intent and confuses the owner (UAT 2026-06-17: "Hard effort" on a Warmup). */
  let isWarmupOrCooldown = $derived(
    set?.setId === "warmup" || set?.setId === "cooldown"
  );

  /** True only when the WHOLE swim has no HR samples to read effort from. In
   *  that case the story card shows one honest line where the badge would sit,
   *  instead of a silently empty slot. Keyed on dw.hrSeries — NEVER on the
   *  set-level stats (those are null for an all-excluded set even on a swim
   *  full of HR; the old set-keyed guard rendered a false "No heart rate
   *  recorded" on the solo swim's lone-phantom Warmup). */
  let noHeartRate = $derived(swimLacksHeartRate(dw.hrSeries));

  /** Every member lap excluded → the story card carries ONLY the honest
   *  "looks off" line: no story, badge, Best/avg, sparkline, or no-HR line. */
  let allExcluded = $derived(set ? allLapsExcluded(set) : false);

  /** Detected palindrome — the Rockalita label is the v1 trigger. */
  let isPalindrome = $derived(set?.label === "The Rockalita");

  /** Best / slowest INCLUDED lap by duration (setMath — kills the phantom
   *  "Best: 9.2s Butterfly" lie; stroke spelled out). Null below 2 included. */
  let bestWorst = $derived(set ? setBestWorst(set) : null);

  /** 1-based position of the fastest lap within this set's lap list. */
  let fastestPos = $derived(
    bestWorst ? set.laps.findIndex((l) => l.index === bestWorst.fastest.index) + 1 : null,
  );
  /** 1-based position of the slowest lap within this set's lap list. */
  let slowestPos = $derived(
    bestWorst ? set.laps.findIndex((l) => l.index === bestWorst.slowest.index) + 1 : null,
  );

  /** Distinct stroke styles present in this set, in first-appearance order. */
  let setStrokes = $derived(set ? [...new Set(set.laps.map((l) => l.strokeStyle))] : []);

  let story = $derived(
    set ? storyFor(set, dw.setGroups, set.aiStory, $showcase) : "",
  );

  // ----- per-set HR story: ECharts red-line chart (SetHrLineChart) + avg/peak BPM -----

  /** Set-level avg + peak BPM. Null when this set's window has no samples —
   *  even if the swim has HR elsewhere — so the line is omitted silently. */
  let hrStats = $derived(
    set ? setHrStats(dw.hrSeries, set.startMs, set.endMs) : null,
  );

  let hrLineChart = $derived.by(() => {
    if (!set) return null;
    return buildSetHrLineChart(set.laps, dw.hrSeries, set.startMs, set.endMs);
  });

  /** Plain-words "N min rest before", or "" when there's no rest to show. */
  let restText = $derived(
    set && set.restBeforeMs !== null ? fmtRest(set.restBeforeMs, "rest before") : "",
  );
  /** Start/end ms for the header time line. Null when set has no valid timestamps. */
  let timeRange = $derived(set ? setTimeRange(set) : null);
  /** Whether the meta row has ANY content. When false the row is omitted
   *  entirely so a missing effort badge never leaves an empty slot that reads as
   *  a failed render (P3). The no-HR line counts as content — it replaces the
   *  badge with an honest sentence rather than nothing. */
  let hasMeta = $derived(badge !== null || restText !== "" || noHeartRate);

  function close() {
    closeOverlay("set-detail");
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape") close();
  }

  // Lock background scroll while open (mirrors RingConfigSheet).
  $effect(() => {
    if (!open) return;
    lockBodyScroll();
    return () => unlockBodyScroll();
  });

  /** The length whose stroke picker is open (expanded in place), or null. */
  let pickerIndex = $state<number | null>(null);

  // Reset picker state when the sheet closes so reopening is always clean.
  $effect(() => {
    if (!open) pickerIndex = null;
  });

  // Deep-link: the suspects card opens the sheet with fixFirst — jump to the
  // first suspect's picker exactly once per open (re-derives must not re-jump).
  // The suspect deck's "Something else…" instead passes fixLapIndex, which
  // wins over fixFirst — it names an EXACT length, so falling back to "first
  // suspect" would open the wrong picker.
  let fixFirstDone = $state(false);
  $effect(() => {
    if (!open) { fixFirstDone = false; return; }
    if ((fixFirst || fixLapIndex !== undefined) && !fixFirstDone) {
      fixFirstDone = true;
      if (fixLapIndex !== undefined) {
        pickerIndex = fixLapIndex;
        void tick().then(() => {
          document.querySelector(`[data-lap-index="${fixLapIndex}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        });
      } else {
        void reviewFirstSuspect();
      }
    }
  });

  function togglePicker(lap: Lap) {
    // Showcase is a look-don't-touch public page: the picker panel is already
    // gated behind !$showcase (line ~467), but without this guard the tap and
    // keyboard handlers below still flip pickerIndex, so the card raises,
    // takes the accent style, and the chevron rotates open with no panel
    // underneath it — a half-activated, broken-looking card.
    if ($showcase) return;
    haptic("selection");
    pickerIndex = pickerIndex === lap.index ? null : lap.index;
  }

  /** The four standard strokes as tiles. Colors come from the ONE canonical
   *  stroke palette (strokeColor) so the picker matches the chart, the ribbon,
   *  and the row swatch exactly — no second palette to drift (Scott UAT,
   *  2026-06-17). label = short display; stroke = the canonical value written. */
  /** Core four — the strokes Apple Watch assigns natively. */
  const CORE_TILES = [
    { label: "Free",   stroke: "Freestyle"   },
    { label: "Back",   stroke: "Backstroke"  },
    { label: "Breast", stroke: "Breaststroke" },
    { label: "Fly",    stroke: "Butterfly"   },
  ] as const;

  /** Non-core two — technique and classification labels, not Watch strokes.
   *  Colors already live in STROKE_COLORS (designTokens.ts lines 67, 56). */
  const EXTRA_TILES = [
    { label: "Drill", stroke: "Drill" },
    { label: "Mixed", stroke: "Mixed" },
  ] as const;

  function strokesFor(lap: Lap): number | null {
    return lap.strokeCount;
  }
  /** Seconds per length — the second raw fact shown next to strokes (G9). */
  function secondsFor(lap: Lap): number {
    return Math.round(lap.durationSeconds);
  }

  /** One-tap commit: write a stroke-correction label for this length and persist
   *  + re-derive through the shared path, preserving any exclusion decision. */
  function fixStroke(lap: Lap, stroke: string) {
    const labels = get(currentLabels);
    const prev = priorLabelFor(labels, lap.index, lap.correctionKey);
    const suspect = suspectByIndex.get(lap.index) ?? null;
    commitLabelChange(upsertLabel(labels, buildStrokeCorrection(lap, stroke, prev, suspect)));
    if (get(saveStatus) === "failed") {
      haptic("error");
      return;
    }
    haptic("medium");
    pickerIndex = null;
  }

  /** "I looked, the watch was right": writes a user-confirmed resolution label
   *  (no stroke change) so this length stops being flagged — permanently. */
  function confirmWatch(lap: Lap) {
    const labels = get(currentLabels);
    const prev = priorLabelFor(labels, lap.index, lap.correctionKey);
    const suspect = suspectByIndex.get(lap.index) ?? null;
    commitLabelChange(upsertLabel(labels, buildWatchConfirmation(lap, prev, suspect)));
    if (get(saveStatus) === "failed") {
      haptic("error");
      return;
    }
    haptic("medium");
    pickerIndex = null;
  }

</script>

<svelte:window onkeydown={onKey} />

{#if open && set}
  <div
    class="scrim"
    role="presentation"
    onclick={close}
    onkeydown={(e) => e.key === "Enter" && close()}
  ></div>
  <div
    class="sheet"
    role="dialog"
    aria-modal="true"
    aria-label={set.label}
    use:sheetDrag={close}
  >
    <!-- Oura-style back: top-left chevron, sticky at the top -->
    <div class="sheet-header">
      <!-- The grabber every other sheet shows: the cue that a drag down closes it. -->
      <div class="grabber" aria-hidden="true"></div>
      <button type="button" class="back-chevron" onclick={close} aria-label="Back to your swim">
        <span aria-hidden="true">‹</span>
      </button>
    </div>
    <div class="content">
      <!-- 2. Title + meta -->
      <h1 class="title">{set.label}</h1>
      <p class="meta">
        {set.meters}m · {set.laps.length} length{set.laps.length === 1 ? "" : "s"} ·
        {fmtMSS(set.durationSeconds)} of swimming
      </p>
      {#if !$showcase && timeRange}
        <p class="meta-time">
          {fmtTimeOfDay(timeRange.startMs)} – {fmtTimeOfDay(timeRange.endMs)}
        </p>
      {/if}

      <!-- 4. Palindrome callout -->
      {#if isPalindrome}
        <div class="callout-pill">Strokes mirror front to back</div>
      {/if}

      <!-- 5. Story card -->
      <div class="story-card">
        {#if allExcluded}
        <!-- Every member excluded: ONE honest jargon-free line, nothing else —
             no story, badge, Best/avg, sparkline, or no-HR line. Copy lives in
             setMath.looksOffLine (singular/plural agreement is test-pinned). -->
        <p class="looks-off">{looksOffLine(set)}</p>
        {:else}
        {#if story}
          <p class="story-sentence">{story}</p>
        {/if}
        {#if hasMeta}
          <div class="story-meta">
            {#if badge && !isWarmupOrCooldown}
              <!-- Badge text picked per fill (contrastTextOn): fixed bg-canvas
                   text was dark-on-fire for a Hard badge (muddy) and weak on
                   the Easy blue; Moderate keeps dark-on-yellow. -->
              <span
                class="effort-badge"
                style:background={effortColor(badge)}
                style:color={contrastTextOn(effortColor(badge))}
              >{badge} effort</span>
            {:else if noHeartRate}
              <!-- showcase reframes the no-HR line as a neutral by-design fact
                   (no apology, no em dash). The published swim hides HR on
                   purpose; the app's own copy is unchanged off the public page. -->
              <span class="no-hr"
                >{$showcase
                  ? "Effort data stays private on this swim."
                  : "No heart rate recorded, so effort labels aren't available for this swim."}</span>
            {/if}
            {#if restText !== ""}
              <span class="story-rest">{restText}</span>
            {/if}
          </div>
        {/if}
        {#if bestWorst}
          <div class="best-chips">
            <div class="best-chip">
              <div class="best-chip-label">Fastest length</div>
              <div class="best-chip-value">Length {fastestPos} · {bestWorst.fastest.strokeStyle} {fmtSeconds1(bestWorst.fastest.durationSeconds)}s</div>
            </div>
            <div class="best-chip">
              <div class="best-chip-label">Slowest length</div>
              <div class="best-chip-value">Length {slowestPos} · {bestWorst.slowest.strokeStyle} {fmtSeconds1(bestWorst.slowest.durationSeconds)}s</div>
            </div>
          </div>
        {/if}

        <!-- Per-set HR story: the SetHrLineChart ECharts red-line chart (smooth
             coral HR line, slim stroke-color ribbon along the top, dashed avg
             reference line, x-axis = length number) + the avg/peak BPM stat line.
             Omitted silently when this set's window holds no samples — the no-HR
             honest line above covers the whole-swim case. -->
        {#if hrStats}
          {#if hrLineChart}
            <SetHrLineChart data={hrLineChart} />
            <div class="chart-legend">
              {#each setStrokes as s (s)}
                <span class="chart-legend-item">
                  <span class="chart-legend-dot" style:background={strokeColor(s)} aria-hidden="true"></span>
                  {s}
                </span>
              {/each}
            </div>
          {/if}
          <p class="hr-stat">
            <span class="hr-num">{Math.round(hrStats.avg)}</span>{" "}<span class="hr-lbl">avg</span>
            <span class="hr-dot" aria-hidden="true">·</span>
            <span class="hr-num">{Math.round(hrStats.peak)}</span>{" "}<span class="hr-lbl">peak BPM</span>
          </p>
          {#if hrLineChart && hrLineChart.lengths.filter((l) => l.bpm !== null).length < hrLineChart.lapCount}
            <p class="hr-coverage-note">Heart rate on {hrLineChart.lengths.filter((l) => l.bpm !== null).length} of {hrLineChart.lapCount} lengths.</p>
          {/if}
        {/if}
        {/if}
      </div>

      <!-- 6. Length cards -->
      {#if $saveStatus === "failed"}
        <div class="save-failed" role="alert">Couldn't save your last change. Try it again.</div>
      {/if}
      <!-- The "Lengths" header + the "Fix a stroke ›" action make the edit job
           spatially obvious (it lives at the list). Each row's chevron + the
           per-row picker carry "tap to fix" from there. -->
      <div class="lengths-header">
        <span class="lengths-title">Lengths</span>
        <!-- Showcase is a look-don't-touch public page: hide the owner editor. -->
        {#if !$showcase}
          <button
            type="button"
            class="fix-stroke-action"
            onclick={reviewFirstSuspect}
            onkeydown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); reviewFirstSuspect(); } }}
          >Fix a stroke ›</button>
        {/if}
      </div>
      <div class="lap-cards">
        {#each set.laps as lap, lapPos (lap.index)}
          {@const excludedNote = lap.effectiveExcluded ? `${lap.exclusionReason ?? "Not counted"}. ` : ""}
          {@const isFixed = correctedLapIndices.has(lap.index)}
          <div
            class="lap-card"
            data-lap-index={lap.index}
            class:not-counted={lap.effectiveExcluded}
            class:fixed={isFixed}
            class:picking={pickerIndex === lap.index}
            use:tapGuard={() => togglePicker(lap)}
            onkeydown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); togglePicker(lap); } }}
            role="button"
            tabindex="0"
            aria-label={excludedNote +
              `Length ${lapPos + 1}, ${lap.strokeStyle}, ${fmtSeconds1(lap.durationSeconds)} seconds.` +
              (isFixed ? ` You fixed this: the watch recorded ${lap.watchStrokeStyle}.` : "") +
              (suspectByIndex.has(lap.index) ? " Might be wrong." : "")}
          >
            <!-- Top face: length number + stroke name (tier 1) + strokes/seconds (tier 2). -->
            <div class="lap-face">
              <span class="lap-swatch" style:background={strokeColor(lap.strokeStyle)}></span>
              <div class="lap-main">
                <div class="lap-tier1">
                  <span class="lap-num">{lapPos + 1}<span class="lap-num-dot"> ·</span></span>
                  <span class="lap-stroke">
                    {lap.strokeStyle}
                  </span>
                  <!-- Deliberately a SIBLING of .lap-stroke, not a child of it. An
                       excluded length dims .lap-num/.lap-stroke, and a child cannot
                       escape an ancestor's opacity — it composites the whole subtree.
                       "You fixed this" stays true whether or not the length counts,
                       and on a fixed+excluded row it was the dimmest thing on screen
                       while carrying the most information. Same standing as the
                       "Not counted in your totals" line, which is also exempt. -->
                  {#if isFixed}
                    <span class="fixed-mark">✓ Fixed</span>
                  {/if}
                  {#if suspectByIndex.has(lap.index)}
                    <span class="suspect-mark">Might be wrong</span>
                  {/if}
                </div>
                {#if lap.effectiveExcluded}
                  <!-- Labeled, not deleted: an excluded length keeps its stroke
                       and time but never wears an effort word the stats don't
                       count. Shows the WHY in short jargon-free copy (TW8 dec. 3)
                       — "Glitch — too short" / "Unusually slow" / "You marked
                       this out" — falling back to "Not counted" when no reason.
                       Same high-contrast .lap-not-counted label; CSS unchanged. -->
                  <div class="lap-not-counted">{lap.exclusionReason ?? "Not counted"}</div>
                {:else}
                  <div class="lap-tier2">
                    {#if strokesFor(lap) !== null}{strokesFor(lap)} strokes · {/if}{secondsFor(lap)}s
                  </div>
                {/if}
              </div>
              <div class="lap-time">{fmtSeconds1(lap.durationSeconds)}s</div>
              <!-- Single clear "open me" affordance: a trailing disclosure
                   chevron (replaces the old faint twin-bar swipe grip, which all
                   four reviewers read as a divider, not a tap target). Rotates
                   down when the picker is open. -->
              <span class="lap-chevron" class:open={pickerIndex === lap.index} aria-hidden="true">›</span>
            </div>
            {#if !$showcase && pickerIndex === lap.index}
              <!-- Shield the whole picker from the tapGuard: a finger-down inside
                   the picker would otherwise bubble to .lap-card and togglePicker
                   would dismiss it. This container-level stopper covers the whole
                   picker, tiles included. Do NOT add per-tile onpointerdown
                   stopPropagation: it breaks the pointer->click synthesis on iOS
                   Safari / Chromium, so the tile never fires onclick (fixStroke
                   never runs) — the exact "cannot change a stroke" bug from
                   2026-06-17. -->
              <div class="fix-picker" role="presentation" onpointerdown={(e) => e.stopPropagation()}>
                {#if suspectByIndex.get(lap.index)}
                  {@const sus = suspectByIndex.get(lap.index)!}
                  {@const shortLabel = CORE_TILES.find((t) => t.stroke === sus.suggestedStroke)?.label ?? sus.suggestedStroke}
                  <div class="fix-suggestion">
                    Looks more like <strong>{shortLabel}</strong>.{#if sus.reasons[0]}{" "}{sus.reasons[0][0].toUpperCase() + sus.reasons[0].slice(1)}.{/if}
                  </div>
                {:else}
                  <div class="fix-said-short">Watch said {lap.watchStrokeStyle}.</div>
                {/if}
                {#if suspectByIndex.has(lap.index)}
                  <button
                    type="button"
                    class="fix-watch-right"
                    onclick={(e) => { e.stopPropagation(); confirmWatch(lap); }}
                  >
                    ✓ The watch was right
                  </button>
                {/if}
                <div class="fix-tiles">
                  <div class="fix-tiles-row">
                    {#each CORE_TILES as tile (tile.stroke)}
                      <button
                        type="button"
                        class="fix-tile"
                        class:is-selected={tile.stroke === lap.strokeStyle && !suspectByIndex.has(lap.index)}
                        class:is-suggested={suspectByIndex.get(lap.index)?.suggestedStroke === tile.stroke}
                        style:background={strokeColor(tile.stroke)}
                        style:color={contrastTextOn(strokeColor(tile.stroke))}
                        onclick={(e) => {
                          e.stopPropagation();
                          if (tile.stroke === lap.strokeStyle) {
                            haptic("selection");
                            const el = e.currentTarget as HTMLElement;
                            el.classList.add("tile-pulse");
                            setTimeout(() => el.classList.remove("tile-pulse"), 180);
                            return;
                          }
                          fixStroke(lap, tile.stroke);
                        }}
                      >
                        {tile.label}
                        <span class="tile-check" aria-hidden="true">✓</span>
                        {#if suspectByIndex.get(lap.index)?.suggestedStroke === tile.stroke}
                          <span class="tile-hint">best match</span>
                        {/if}
                      </button>
                    {/each}
                  </div>
                  <div class="fix-tiles-gap" aria-hidden="true"></div>
                  <div class="fix-tiles-row fix-tiles-row--extra">
                    {#each EXTRA_TILES as tile (tile.stroke)}
                      <button
                        type="button"
                        class="fix-tile"
                        class:is-selected={tile.stroke === lap.strokeStyle && !suspectByIndex.has(lap.index)}
                        style:background={strokeColor(tile.stroke)}
                        style:color={contrastTextOn(strokeColor(tile.stroke))}
                        onclick={(e) => {
                          e.stopPropagation();
                          if (tile.stroke === lap.strokeStyle) {
                            haptic("selection");
                            const el = e.currentTarget as HTMLElement;
                            el.classList.add("tile-pulse");
                            setTimeout(() => el.classList.remove("tile-pulse"), 180);
                            return;
                          }
                          fixStroke(lap, tile.stroke);
                        }}
                      >
                        {tile.label}
                        <span class="tile-check" aria-hidden="true">✓</span>
                      </button>
                    {/each}
                  </div>
                </div>
                <button
                  type="button"
                  class="fix-cancel"
                  onclick={(e) => { e.stopPropagation(); pickerIndex = null; }}
                >
                  Never mind
                </button>
              </div>
            {/if}
          </div>
        {/each}
      </div>
      <p class="watch-footnote">Your watch picked these strokes, except the ones you fixed.</p>
      <button type="button" class="done-btn" onclick={close}>Done</button>
    </div>
  </div>
{/if}

<style>
  .scrim {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 50;
    animation: scrim-in var(--dur-card) var(--ease-swim);
    touch-action: none;
  }
  .sheet {
    position: fixed;
    inset: 0;
    z-index: 51;
    background: var(--bg-canvas);
    animation: sheet-in var(--dur-card) var(--ease-swim);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    /* pan-y: browser keeps VERTICAL scroll, element owns HORIZONTAL (same
       pattern as ReviewCard.svelte). NEVER touch-action: none (WKWebView trap). */
    touch-action: pan-y;
    overscroll-behavior: contain;
  }
  .content {
    max-width: 430px;
    margin: 0 auto;
    padding: var(--space-2) var(--space-4) calc(var(--space-5) + env(safe-area-inset-bottom, 0px));
    display: flex;
    flex-direction: column;
  }

  /* 2. Title + meta */
  .title {
    font-size: 28px;
    font-weight: 700;
    letter-spacing: -0.3px;
    color: var(--text-primary);
    margin: 0;
  }
  .meta {
    font-size: 16px;
    color: var(--text-muted);
    margin: 4px 0 0;
    font-variant-numeric: tabular-nums;
  }
  /* Start/end time of day — dim, private-only (hidden in showcase mode).
     Font size 15px, tabular nums, color var(--text-dim) to sit below the muted
     meta line in visual hierarchy. No seconds; 12-hour AM/PM. */
  .meta-time {
    font-size: 15px;
    color: var(--text-dim);
    margin: 2px 0 0;
    font-variant-numeric: tabular-nums;
  }

  /* 4. Callout pill */
  .callout-pill {
    align-self: center;
    width: fit-content;
    font-size: 17px;
    font-weight: 500;
    color: var(--accent-primary);
    background: var(--bg-raised);
    padding: 9px 18px;
    border-radius: 999px;
    margin-top: var(--space-3);
  }

  /* 5. Story card */
  .story-card {
    margin-top: var(--space-4);
    background: var(--bg-card);
    border: 1px solid var(--divider);
    border-radius: 16px;
    padding: var(--space-4);
  }
  .story-sentence {
    font-size: 19px;
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.35;
    margin: 0;
  }
  /* All-excluded set: the single honest line the story card carries. Muted
     (it's a disclosure, not a story) but 17px — it IS the load-bearing string
     on this card. Jargon-free: never "phantom" / "artifact" / "excluded". */
  .looks-off {
    font-size: 17px;
    font-weight: 500;
    color: var(--text-muted);
    line-height: 1.4;
    margin: 0;
  }
  .story-meta {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
    flex-wrap: wrap;
  }
  .effort-badge {
    font-size: 15px;
    font-weight: 700;
    /* color set inline per fill via contrastTextOn() */
    padding: 7px 14px;
    border-radius: 999px;
  }
  .story-rest {
    font-size: 15px;
    color: var(--text-muted);
  }
  /* Honest no-HR line, in the slot the effort badge would occupy. One muted
     plain sentence so a swim with no heart-rate data says so, rather than
     leaving the effort badge silently absent. Shown ONLY when the whole swim
     lacks HR — a single lap missing samples stays silent. */
  .no-hr {
    font-size: 15px;
    color: var(--text-muted);
  }
  /* Fastest / Slowest chips — side by side, card background, muted label + bright value */
  .best-chips {
    display: flex;
    gap: 8px;
    margin-top: 12px;
  }
  .best-chip {
    flex: 1;
    min-width: 0;
    background: var(--bg-raised);
    border-radius: 10px;
    padding: 8px 12px;
  }
  .best-chip-label {
    font-size: 11px;
    font-weight: 600;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin-bottom: 2px;
  }
  .best-chip-value {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    font-variant-numeric: tabular-nums;
  }

  /* Stroke color legend — sits immediately under the HR chart, inside {#if hrLineChart}.
     Explains the stroke-color ribbon along the chart's top edge. */
  .chart-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 16px;
    margin-top: 8px;
    padding: 0 2px;
  }
  .chart-legend-item {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 13px;
    font-weight: 500;
    color: #C8D0DA; /* readable on dark; matches .lap-tier2, never text-muted */
  }
  .chart-legend-dot {
    width: 11px;
    height: 11px;
    border-radius: 3px;
    flex-shrink: 0;
  }

  /* Set-level avg/peak line — the numbers in the fire accent, labels + unit
     muted. ≥15px floor. This is the ONLY place a BPM number appears in the
     sheet. */
  .hr-stat {
    font-size: 16px;
    margin: 8px 0 0;
    font-variant-numeric: tabular-nums;
  }
  .hr-num {
    color: var(--accent-fire);
    font-weight: 700;
  }
  .hr-lbl {
    color: var(--text-muted);
    font-weight: 400;
  }
  .hr-dot {
    color: var(--text-dim);
    margin: 0 6px;
  }
  /* Muted note shown only when some lengths lack HR data. Explains gaps in
     the sparse-mode dot chart without alarming the user. */
  .hr-coverage-note {
    font-size: 12px;
    color: var(--text-dim);
    margin: 4px 0 0;
  }

  /* 6. Length cards */
  /* Failed-save notice: surfaces both the synchronous C2a refusal (labels
     failed to load) and an async bridge persist failure above the sheet
     overlay — the SwipeDeck banner underneath is invisible while this sheet
     is open. Fire accent so it reads as urgent without a scary icon. */
  .save-failed {
    margin: 0 0 0.6rem;
    padding: 0.6rem 0.75rem;
    background: var(--accent-fire);
    color: var(--bg-canvas);
    border-radius: 8px;
    font-size: 0.85rem;
    font-weight: 700;
  }
  .lengths-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: var(--space-4);
    margin-bottom: 10px;
  }
  .lengths-title {
    font-size: 20px;
    font-weight: 700;
    color: var(--text-primary);
    letter-spacing: -0.3px;
  }
  .fix-stroke-action {
    font-size: 15px;
    font-weight: 600;
    color: var(--accent-primary);
    background: none;
    border: none;
    padding: 6px 2px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .lap-cards {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .lap-card {
    /* Column: the stroke/time face on top, the detail line below. */
    display: flex;
    flex-direction: column;
    background: var(--bg-card);
    border-radius: 14px;
    padding: 13px 16px;
    min-height: 72px;
    box-sizing: border-box;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    cursor: pointer;
  }
  .lap-card.picking {
    background: var(--bg-raised);
    /* Legible accent border so a low-vision founder can see WHICH row is in edit
       mode (the old rgba(255,255,255,0.22) was near-invisible on the dark card).
       Neutral accent, reads as "active", not a stroke color. */
    border: 1px solid var(--accent-primary);
  }
  /* The top face: stroke chip · stroke name + effort · time, with the always-on
     "N strokes · Ns" detail line below it (the two raw facts, G9). */
  .lap-face {
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .lap-swatch {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    flex-shrink: 0;
  }
  .lap-main {
    flex: 1;
    min-width: 0;
  }
  .lap-tier1 {
    display: flex;
    align-items: baseline;
    /* Must wrap. The "✓ Fixed" badge is a flex sibling here (it cannot live inside
       .lap-stroke, which is dimmed on an excluded row), and a long stroke name plus
       the badge exceeds the row's width budget — without wrap it overflowed straight
       across the time digits. Rows with no badge are unaffected. */
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 3px;
  }
  .lap-num {
    font-size: 15px;
    font-weight: 500;
    color: var(--text-muted);
    flex-shrink: 0;
  }
  .lap-num-dot {
    font-weight: 400;
  }
  /* lap-stroke: bold stroke name, now inline in .lap-tier1. */
  .lap-stroke {
    font-size: 22px;
    font-weight: 700;
    color: var(--text-primary);
  }
  /* Tier 2: elevated contrast so the strokes/seconds facts are never the
     dimmest text on the row. #C8D0DA sits between text-muted (#8F9098) and
     text-primary (#E4E2E4). */
  .lap-tier2 {
    font-size: 14px;
    font-weight: 500;
    color: #C8D0DA;
    font-variant-numeric: tabular-nums;
  }
  /* Excluded lengths (rest, outlier, split-length artifact): visible but
     visibly labeled. The stroke swatch/name, time, and swipe affordances dim —
     NOT the "Not counted" label, which stays full-opacity high-contrast: the
     dismissal signal must be the MOST legible thing on the card, not the
     least (limited-eyesight founder; panel gate on 8c72b12). Opacity sits on
     the dimmed pieces, never the card root — a child can't exceed its
     parent's opacity. The list can never contradict the Best/avg math above
     it (the 9.2s phantom looked exactly like a real length). */
  /* Dim the excluded row's TEXT children individually rather than the whole
     .lap-tier1 container: opacity on a container composites its entire subtree,
     so a "✓ Fixed" mark nested inside it could never be exempted. */
  .lap-card.not-counted .lap-swatch,
  .lap-card.not-counted .lap-num,
  .lap-card.not-counted .lap-stroke,
  .lap-card.not-counted .lap-time,
  .lap-card.not-counted .lap-chevron,
  .lap-card.not-counted .lap-tier2 {
    opacity: 0.55;
  }
  /* Persona panel B4: a length that is BOTH fixed and excluded still dimmed its
     own stroke name, so the "✓ Fixed" badge sat next to the faintest text on
     the row — for the correction, the stroke name IS the headline fact.
     Higher specificity (4 classes) wins over the rule above regardless of
     source order. Plain excluded rows (not fixed) keep the dimming. */
  .lap-card.not-counted.fixed .lap-stroke {
    opacity: 1;
  }
  .lap-not-counted {
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
    margin-top: 2px;
  }
  .lap-time {
    font-size: 22px;
    font-weight: 700;
    color: var(--text-primary);
    font-variant-numeric: tabular-nums;
  }
  /* Trailing disclosure chevron — the single clear "tap to open" affordance at
     the right edge of every row. Replaces the old faint twin-bar grip (which
     read as a divider). Clearly visible (text-muted, not dim), vertically
     centered by the flex face, and rotates to point down when its picker opens. */
  .lap-chevron {
    flex-shrink: 0;
    margin-left: 2px;
    font-size: 26px;
    line-height: 1;
    font-weight: 400;
    color: var(--text-muted);
    transition: transform 0.18s ease-out;
  }
  .lap-chevron.open {
    transform: rotate(90deg);
    color: var(--accent-primary);
  }

  .fix-picker {
    padding: 12px 14px 14px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }
  /* Non-flagged length: the picker still needs to say what the watch has it
     as (belt-and-braces context; rings already carry this on the tile but a
     length with the sheet open should read stand-alone). Muted, one line,
     smaller margin than the old two-line prompt+said pair it replaces. */
  .fix-said-short {
    font-size: 14px;
    color: var(--text-muted);
    margin-bottom: 4px;
  }
  /* The spotter's actual suggestion and the reason behind it. This is the
     load-bearing "we think you swam X" signal, so it is bright and prominent
     (an earlier version buried it as a tiny in-tile "looks like this" cue that
     read as a stroke-technique hint, not a machine suggestion). Naming the
     reason — e.g. "more strokes than your Backstroke lengths" — also explains
     WHY this length and not the slowest one. */
  .fix-suggestion {
    font-size: 15px;
    font-weight: 600;
    color: var(--accent-primary);
    margin-bottom: 10px;
    line-height: 1.35;
  }
  .fix-suggestion strong {
    font-weight: 800;
  }
  .fix-tiles {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .fix-tiles-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
  }
  .fix-tiles-row--extra {
    grid-template-columns: repeat(2, 1fr);
  }
  .fix-tiles-gap {
    height: 0.35rem;
  }
  .fix-tile {
    position: relative;
    min-height: 72px;
    border: none;
    border-radius: 14px;
    color: #fff;
    font-size: 17px;
    font-weight: 700;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .fix-tile:active {
    filter: brightness(1.12);
  }
  /* Selected tile: 2px ring at full stroke color, not just background change.
     Ring uses outline (not border) to avoid layout shift. The check mark
     provides shape redundancy so selection is readable without color alone. */
  .fix-tile.is-selected {
    outline: 2px solid currentColor;
    outline-offset: 2px;
    opacity: 1;
  }
  /* Unselected tiles dim to make the selection obvious. */
  .fix-tile:not(.is-selected) {
    opacity: 0.55;
  }
  /* When no tile is selected (picker freshly opened and lap has an unknown
     stroke not in any tile), all tiles show at full opacity — never hide them. */
  .fix-tiles:not(:has(.fix-tile.is-selected)) .fix-tile {
    opacity: 1;
  }
  /* Check mark: bottom-right corner, hidden until selected. */
  .tile-check {
    display: none;
    position: absolute;
    bottom: 6px;
    right: 8px;
    font-size: 14px;
    font-weight: 800;
    line-height: 1;
  }
  .fix-tile.is-selected .tile-check {
    display: block;
  }
  /* No-op tap on already-selected tile: brief opacity flicker, 180ms.
     Added/removed via classList directly to avoid Svelte re-render on the hot path. */
  @keyframes tile-pulse {
    0%   { opacity: 1; }
    40%  { opacity: 0.65; }
    100% { opacity: 1; }
  }
  /* :global() because tile-pulse is added/removed via classList, not Svelte class binding */
  :global(.fix-tile.tile-pulse) {
    animation: tile-pulse 180ms ease-out;
  }

  /* Oura-style top-left back chevron, sticky at the top of the sheet. Replaces
     the old fixed bottom "Back to your swim" text bar. Pairs with a left-edge
     swipe-back (added separately) so reach stays easy one-handed. */
  .sheet-header {
    position: sticky;
    top: 0;
    z-index: 53;
    background: var(--bg-canvas);
    padding-top: env(safe-area-inset-top, 0px);
    display: flex;
    align-items: center;
  }
  /* The other sheets' grabber, top centre just below the status bar. */
  .grabber {
    position: absolute;
    top: calc(env(safe-area-inset-top, 0px) + 8px);
    left: 50%;
    width: 36px;
    height: 4px;
    margin-left: -18px;
    border-radius: 999px;
    background: var(--divider-strong);
  }
  .back-chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    margin: 2px 0 0 6px;
    background: transparent;
    border: none;
    color: var(--accent-primary);
    font-size: 34px;
    line-height: 1;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .back-chevron:active {
    opacity: 0.55;
  }

  .watch-footnote {
    font-size: 12px;
    color: var(--text-dim);
    margin-top: 14px;
    margin-left: 4px;
    line-height: 1.4;
  }
  /* Sticky Done button at the bottom of .content. In-flow (not position:fixed)
     so the user reaches it by scrolling — confirms they've seen the full set.
     z-index audit: .done-btn is inside .sheet (z-index:51 stacking context).
     ECharts canvas is also inside .sheet with no own z-index, so it cannot
     escape above the Done button within the sheet's stacking context.
     No additional position/z-index needed on this element. */
  /* Done button: clearly lighter fill than the dark sheet so it reads as the
     primary way out. rgba(255,255,255,0.16) lifts it well above var(--bg-raised)
     (≈0.06 effective), and the border adds a crisp edge for low-vision clarity.
     Calm, non-gamified — same family as .fix-cancel but unmistakably primary. FIX 4. */
  .done-btn {
    display: block;
    width: 100%;
    min-height: 52px;
    margin-top: var(--space-4);
    background: rgba(255, 255, 255, 0.16);
    color: var(--text-primary);
    border: 1px solid rgba(255, 255, 255, 0.28);
    border-radius: 14px;
    font-size: 17px;
    font-weight: 600;
    letter-spacing: -0.005em;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .done-btn:active {
    opacity: 0.65;
  }
  .done-btn:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 2px;
  }
  /* Owner-correction mark ("mark everywhere" override, review 2026-07-12):
     text+shape, not a bare glyph or colour swap, so it survives low vision and
     colour-blindness. The accessible name lives on the PARENT .lap-card's
     aria-label (a child span is never announced) — this is the visible half. */
  /* Never inherits currentColor: on an excluded row that resolved to the muted
     grey and the badge — the most informative thing on the row — became the
     faintest. Explicit primary ink, full strength, always. */
  .fixed-mark {
    margin-left: 6px;
    padding: 1px 5px;
    border-radius: 3px;
    border: 1px solid var(--text-primary);
    color: var(--text-primary);
    font-size: 0.7rem;
    font-weight: 600;
    white-space: nowrap;
    flex-shrink: 0;
  }
  /* Word chip, same family as .fixed-mark (review round 2, 2026-07-12): the old
     unlabeled grey "?" glyph had no word for eyes, only an aria-label a nested
     span never gets to announce anyway (accessible name lives on the parent
     .lap-card, same as .fixed-mark). Dashed border (vs. .fixed-mark's solid)
     is the non-colour way to tell the two chips apart at a glance. Explicit
     primary ink, never currentColor — a sibling of .lap-stroke, so it cannot
     inherit an excluded row's dimming. */
  .suspect-mark {
    margin-left: 6px;
    padding: 1px 5px;
    border-radius: 3px;
    border: 1px dashed var(--text-primary);
    color: var(--text-primary);
    font-size: 0.7rem;
    font-weight: 600;
    white-space: nowrap;
    flex-shrink: 0;
  }
  /* Suggested tile emphasis: the LOUDEST element in the picker on a flagged
     length (persona-panel B1, 2026-07-01). A high-contrast ring + slight
     scale/elevation so the detector's actual suggestion visually outranks
     the (now correctly un-highlighted) current-label tile and every other
     tile. Not auto-selected — this is a suggestion, not a written answer.
     z-index keeps the scaled-up tile above its grid siblings. */
  .fix-tile.is-suggested {
    outline: 3px solid var(--text-primary);
    outline-offset: 2px;
    opacity: 1;
    transform: scale(1.05);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.4);
    z-index: 1;
  }
  /* Sub-label inside the suggested tile: "best match" at small font, same
     foreground color as the tile label (inherited via color). Points at WHICH
     tile the .fix-suggestion line names; the suggestion copy carries the why. */
  .tile-hint {
    display: block;
    margin-top: 2px;
    font-size: 11px;
    font-weight: 600;
    opacity: 0.9;
  }

  /* One-tap resolution when the watch's label was actually correct: writes a
     user-confirmed label (no stroke change) so the length stops being flagged.
     Restyled as a real first-class button (persona-panel B2, 2026-07-01) — it's
     the most-used answer and must read as tappable, peer to the stroke tiles,
     not a muted footnote. High-contrast border, larger type, still bold, still
     44px minimum tap target. */
  .fix-watch-right {
    display: block;
    width: 100%;
    min-height: 44px;
    margin: 0.5rem 0 0;
    padding: 0.6rem 0.75rem;
    background: var(--bg-raised);
    color: var(--text-primary);
    border: 2px solid var(--text-primary);
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 700;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  /* Deliberate, taught way out of a mis-opened picker. Full-width, high-contrast,
     neutral (not a stroke color) so a low-vision founder reads it as "close this",
     not as another stroke choice. */
  .fix-cancel {
    margin-top: 10px;
    width: 100%;
    min-height: 44px;
    background: var(--bg-raised);
    color: var(--text-primary);
    border: 1px solid var(--divider);
    border-radius: 12px;
    font-size: 17px;
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  @keyframes scrim-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes sheet-in {
    from { transform: translateY(2%); opacity: 0.6; }
    to { transform: translateY(0); opacity: 1; }
  }
</style>
